import * as React from 'react';

import useRouter from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import { ENTERPRISE_MODAL_NAMES } from 'bundles/unified-common/constants/enterprise';
import { useEnterpriseAutoEnrolledCoursesLoading } from 'bundles/unified-common/contexts/EnterpriseAutoEnrolledCoursesContext';
import EnterpriseModalManagerContext from 'bundles/unified-common/contexts/EnterpriseModalManagerContext';
import {
  useEnterpriseProductFeatures,
  useEnterpriseProductFeaturesLoading,
} from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useProgramMembership } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import useIsEmptyContentError from 'bundles/unified-common/hooks/useEmptyContentError';
import { useShowEnterpriseTermsOfUseModal } from 'bundles/unified-common/hooks/useShowEnterpriseTermsOfUseModal';
import type { ProgramJoinErrorModalInfo } from 'bundles/unified-common/types/enterprise';
import { useAvailableEnrollmentOptions } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseAvailableEnrollmentProgramsContext';
import { useMiniModalRedirectData } from 'bundles/unified-home-common/contexts/enterprise/MiniModalRedirectContext';

type Props = {
  children: React.ReactNode;
};

/**
 * @description This provider aims to find out if we need to surface a modal to the user.
 * If it's determined we should show multiple modals, it decides priority.
 * We use this value primarily in bundles/unified-common/components/EnterpriseModalManager, which
 * uses this value to render the individual modals.
 */
const EnterpriseModalManagerProvider = ({ children }: Props) => {
  const router = useRouter();
  // This state property is driven by the ProgramAccessModal that ENTERPRISE_MODAL_NAMES.PROGRAM_ACCESS maps to.
  const [isAccessModalDismissed, setIsAccessModalDismissed] = React.useState(false);
  // This state property is driven by the AutoEnrolledCoursesModal that ENTERPRISE_MODAL_NAMES.AUTOENROLLED_COURSES maps to.
  const [isAutoenrolledCoursesModalDismissed, setIsAutoenrolledCoursesModalDismissed] = React.useState(false);
  // This state property is driven by the ProgramAtCapacityModal that ENTERPRISE_MODAL_NAMES.PROGRAM_JOIN_ERROR maps to.
  const [programJoinErrorInfo, setProgramJoinErrorInfo] = React.useState<ProgramJoinErrorModalInfo>({
    errorCode: null,
    showModal: false,
  });
  const [programAutoJoinErrorCode, setProgramAutoJoinErrorCode] = React.useState<string | null>(null);
  const [hasShownLoginModal, setHasShownLoginModal] = React.useState(false);
  const features = useEnterpriseProductFeatures();
  const isFeaturesLoading = useEnterpriseProductFeaturesLoading();
  const programMembership = useProgramMembership();
  const programMembershipInstance = programMembership.membership;

  const isAutoEnrolledCoursesLoading = useEnterpriseAutoEnrolledCoursesLoading();
  const availableEnrollmentOptions = useAvailableEnrollmentOptions();
  const thirdPartyOrganization = useThirdPartyOrganization();
  const isInLoginMode = router.location.query.authMode === 'login';

  const isComingFromExternalPlatforms = router.location.query.isExternal === 'true';

  const {
    isLoading: isMiniModalDataLoading,
    courseSlug: miniModalCourseSlug,
    s12nSlug: miniModalS12nSlug,
  } = useMiniModalRedirectData();

  const isEmptyContentError = useIsEmptyContentError();
  const isRestrictedExternalLearner = isComingFromExternalPlatforms && isEmptyContentError;
  // For external platform learners, we enroll them into the program and course directly
  // in the background without showing the program join modal on the unified XDP.
  const showOptimisedLearnerEnrollmentFlow = isComingFromExternalPlatforms;
  const canEnrollIntoCurrentProgramOnXdp =
    showOptimisedLearnerEnrollmentFlow &&
    !isRestrictedExternalLearner &&
    programMembershipInstance?.isEligibleToJoinProgram;
  const {
    showEnterpriseTermsOfUseModal,
    setShowEnterpriseTermsOfUseModal,
    isEnterpriseAdmin,
    shouldShowCCPAMarketingConsent,
    shouldShowGDPRMarketingConsent,
    shouldShowThirdPartyTermsOfConsent,
    isLoading: isEnterpriseConsentLoading,
  } = useShowEnterpriseTermsOfUseModal(thirdPartyOrganization);

  const isModalLoading =
    !!isFeaturesLoading ||
    !!isMiniModalDataLoading ||
    // Treat undefined as loading (provider not initialized yet)
    programMembership?.isMembershipLoading !== false ||
    !!isAutoEnrolledCoursesLoading ||
    !!isEnterpriseConsentLoading ||
    !!availableEnrollmentOptions?.isLoading;

  // Determine if ENTERPRISE_MODAL_NAMES.PRODUCT_DESCRIPTION_PAGE_REDIRECT is able to be shown.
  // We want to redirect to the product description page if legacy query params are present for mini modal
  // and certain other conditions are met. This is only used in legacy contexts and should eventually
  // be deprecated.
  const redirectToProductDescriptionPage =
    router.location.query.productId &&
    router.location.query.showMiniModal &&
    !isMiniModalDataLoading &&
    !hasShownLoginModal &&
    (miniModalCourseSlug || miniModalS12nSlug) &&
    !isInLoginMode &&
    (features?.isBrowseOnlyProgram ||
      canEnrollIntoCurrentProgramOnXdp ||
      !programMembershipInstance?.hasModalToDisplay ||
      isAccessModalDismissed);

  // Determine if ENTERPRISE_MODAL_NAMES.PROGRAM_ACCESS is able to be shown.
  // Do not show this modal when in specific case on enterprise unified XDP where user is not member of current program,
  // but is member of other programs in current org and has other options to enroll.
  // availableEnrollmentOptions.programs will have data if this is the case.
  // In this scenario, this modal will surface the "program deny modal". This is not relevant for learners in this case
  // since they can enroll and could cause confusion.
  // However, we still need to show the modal if email verification is required, as this is a blocker regardless of
  // whether other programs are available.
  const hasOtherEnrollmentOptions = !!(
    availableEnrollmentOptions?.programs?.length || availableEnrollmentOptions?.invitedPrograms?.length
  );
  const needsEmailVerification = !!programMembershipInstance?.isEmailNotVerified;

  const showProgramAccessModal =
    !features?.isBrowseOnlyProgram &&
    !isInLoginMode &&
    !!programMembershipInstance &&
    !isMiniModalDataLoading &&
    programMembershipInstance.hasModalToDisplay &&
    !programJoinErrorInfo.showModal &&
    !programJoinErrorInfo.errorCode &&
    !isAccessModalDismissed &&
    !programMembership?.isMembershipLoading &&
    !availableEnrollmentOptions?.isLoading &&
    !canEnrollIntoCurrentProgramOnXdp &&
    // Allow the modal when email verification is needed, even if other enrollment options exist.
    // Only hide when there are other options AND email verification is NOT required.
    (needsEmailVerification || !hasOtherEnrollmentOptions);

  // Determine if ENTERPRISE_MODAL_NAMES.LOGIN is able to be shown.
  // Show login modal if the incoming URL wants to show the legacy mini modal,
  // the user is logged out and we were unable to find product slug info.
  // This likely means the user is trying to view a private course.
  const showLoginModal =
    !isAuthenticatedUser() &&
    router.location.query.productId &&
    router.location.query.showMiniModal &&
    !isInLoginMode &&
    !hasShownLoginModal &&
    !isMiniModalDataLoading &&
    !miniModalCourseSlug &&
    !miniModalS12nSlug &&
    (features?.isBrowseOnlyProgram || !programMembershipInstance?.hasModalToDisplay || isAccessModalDismissed);

  // The optimized learner flow (always enabled) sends an auto-enrollment notification instead of this modal.
  // Prior to flag cleanup, `!enableOptimizedLearnerFlow` (always false in prod) and `!enableOptimisedEnrollmentSetting` gated this expression, making it always false.
  const showAutoEnrolledCoursesModal = false;

  // Determine if ENTERPRISE_MODAL_NAMES.ADD_EMAIL is able to be shown.
  // The modal this maps to has some additional logic for display.
  // Eventually, when we refactor the modals themselves we can move that logic here.
  const showAddEmailModal =
    !features?.isBrowseOnlyProgram &&
    !isInLoginMode &&
    (isAccessModalDismissed || !programMembershipInstance?.hasModalToDisplay);

  const isEnterpriseTnCModalEnabled = features?.enableEnterpriseTermsOfUseModal;
  // Determine if ENTERPRISE_MODAL_NAMES.ENTERPRISE_TERMS_OF_USE is able to be shown.
  // This will currently be controlled by a feature toggle which will then go on
  // to be enabled for all of enterprise
  const shouldShowEnterpriseTermsOfUseModal =
    isAuthenticatedUser() &&
    isEnterpriseTnCModalEnabled &&
    showEnterpriseTermsOfUseModal &&
    programMembershipInstance?.isEligibleToJoinProgram;
  // Determine if ENTERPRISE_MODAL_NAMES.PROGRAM_JOIN_ERROR is able to be shown
  // Since the display of this modal is currently being only controlled by a flow of
  // api errors and its state is being set by children components, we only check for the shown
  // state we maintain here.
  const shouldShowProgramJoinError = programJoinErrorInfo.showModal;

  let modalToShow = null;

  // Multiple values can be true, choose one modal to show based on priority.
  if (shouldShowEnterpriseTermsOfUseModal) {
    modalToShow = ENTERPRISE_MODAL_NAMES.ENTERPRISE_TERMS_OF_USE;
  } else if (showProgramAccessModal) {
    modalToShow = ENTERPRISE_MODAL_NAMES.PROGRAM_ACCESS;
  } else if (shouldShowProgramJoinError) {
    modalToShow = ENTERPRISE_MODAL_NAMES.PROGRAM_JOIN_ERROR;
  } else if (showLoginModal) {
    modalToShow = ENTERPRISE_MODAL_NAMES.LOGIN;
  } else if (redirectToProductDescriptionPage) {
    modalToShow = ENTERPRISE_MODAL_NAMES.PRODUCT_DESCRIPTION_PAGE_REDIRECT;
  } else if (showAutoEnrolledCoursesModal) {
    modalToShow = ENTERPRISE_MODAL_NAMES.AUTOENROLLED_COURSES;
  } else if (showAddEmailModal) {
    modalToShow = ENTERPRISE_MODAL_NAMES.ADD_EMAIL;
  }
  return (
    <EnterpriseModalManagerContext.Provider
      value={{
        modalToShow,
        isAccessModalDismissed,
        setIsAccessModalDismissed,
        isAutoenrolledCoursesModalDismissed,
        setIsAutoenrolledCoursesModalDismissed,
        setHasShownLoginModal,
        setShowEnterpriseTermsOfUseModal,
        showEnterpriseTermsOfUseModal,
        programJoinErrorInfo,
        setProgramJoinErrorInfo,
        programAutoJoinErrorCode,
        setProgramAutoJoinErrorCode,
        isEnterpriseAdmin,
        shouldShowCCPAMarketingConsent,
        shouldShowGDPRMarketingConsent,
        shouldShowThirdPartyTermsOfConsent,
        isLoading: isModalLoading,
      }}
    >
      {children}
    </EnterpriseModalManagerContext.Provider>
  );
};
export default EnterpriseModalManagerProvider;
